import React, { useMemo, useRef } from 'react'
import { useDismissTooltipOnOutsideTouch } from '../../../utils/hooks/useDismissTooltipOnOutsideTouch'
import {
    LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Legend, ReferenceLine
} from 'recharts'
import { Square, Columns2, Grid3x2, LayoutGrid, Loader2 } from 'lucide-react'
import { ChartData, DataPoint } from './hooks/useChartData'

// Loading.tsx é a splash de tela cheia do app (100vh, posições em vw) — dentro do card pequeno
// do gráfico ela fica clipada pelo overflow:hidden e não aparece nada visível. Por isso um
// spinner inline próprio aqui em vez de reaproveitar aquele componente.
const CHART_SPIN_KEYFRAMES = `@keyframes dhm-chart-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

const CARD_STYLE: React.CSSProperties = {
    background: 'rgba(94,94,94,0.1)',
    backdropFilter: 'blur(4px)',
    borderRadius: 20,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
}

const HEADER_STYLE: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    cursor: 'grab',
    userSelect: 'none',
}

const TITLE_STYLE: React.CSSProperties = {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 13,
    fontWeight: 700,
    background: 'linear-gradient(45deg, rgb(62,147,216), rgb(76,185,49))',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '85%',
}

const CLOSE_BTN: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
    padding: '0 4px',
    flexShrink: 0,
}

export const CHART_SIZES = [12, 6, 4, 3] as const
export type ChartSizeW = typeof CHART_SIZES[number]

const SIZE_ICONS: Record<number, React.ReactElement> = {
    12: <Square    size={13} strokeWidth={1.8} />,
    6:  <Columns2  size={13} strokeWidth={1.8} />,
    4:  <Grid3x2   size={13} strokeWidth={1.8} />,
    3:  <LayoutGrid size={13} strokeWidth={1.8} />,
}
const SIZE_TIPS: Record<number, string> = { 12: 'Stretch', 6: 'Duo', 4: 'Trio', 3: 'Quadra' }

// ── Tipos de estatísticas ─────────────────────────────────────────

export type StatKey = 'mean' | 'median' | 'mode' | 'max' | 'min' | 'stdDev'

interface StatResult {
    mean:   number
    median: number
    mode:   number
    max:    number
    min:    number
    stdDev: number
}

// Cor e dash de cada estatística
const STAT_STYLE: Record<StatKey, { stroke: string; dash: string; label: string }> = {
    mean:   { stroke: '#5292D1',              dash: '6 3',  label: 'μ'   },
    median: { stroke: '#92B258',              dash: '6 3',  label: 'Med' },
    mode:   { stroke: '#E8A84C',              dash: '6 3',  label: 'Mo'  },
    max:    { stroke: '#7BB8E8',              dash: '3 3',  label: 'Max' },
    min:    { stroke: '#7BB8E8',              dash: '1 4',  label: 'Min' },
    stdDev: { stroke: 'rgba(255,255,255,0.3)', dash: '8 4', label: 'σ'   },
}

// ── Cálculo de estatísticas ───────────────────────────────────────

function computeStats(points: DataPoint[]): StatResult | null {
    const vals = points.map(p => Number(p.value)).filter(v => isFinite(v))
    if (vals.length < 2) return null

    const n    = vals.length
    const mean = vals.reduce((s, v) => s + v, 0) / n

    const sorted = [...vals].sort((a, b) => a - b)
    const mid    = Math.floor(n / 2)
    const median = n % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]

    // Moda: arredonda para 1 decimal, mais frequente
    const freq: Record<string, number> = {}
    vals.forEach(v => { const k = v.toFixed(1); freq[k] = (freq[k] ?? 0) + 1 })
    const mode = Number(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0])

    const max    = sorted[n - 1]
    const min    = sorted[0]
    const stdDev = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / n)

    return { mean, median, mode, max, min, stdDev }
}

// ── Tooltip customizado (dados + stats) ───────────────────────────

const STAT_KEYS_ORDERED: StatKey[] = ['mean', 'median', 'mode', 'max', 'min', 'stdDev']

/** Renderiza as linhas de stats dentro do tooltip */
function StatRows({ stats, enabledStats }: { stats: StatResult; enabledStats: Set<string> }) {
    const rows: React.ReactNode[] = []
    for (const key of STAT_KEYS_ORDERED) {
        if (!enabledStats.has(key)) continue
        const s = STAT_STYLE[key]
        if (key === 'stdDev') {
            rows.push(
                <div key="+σ" style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: s.stroke }}>
                    <span>+σ</span><span>{fmt(stats.mean + stats.stdDev)}</span>
                </div>,
                <div key="-σ" style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: s.stroke }}>
                    <span>−σ</span><span>{fmt(stats.mean - stats.stdDev)}</span>
                </div>
            )
        } else {
            rows.push(
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: s.stroke }}>
                    <span>{s.label}</span><span>{fmt(stats[key])}</span>
                </div>
            )
        }
    }
    if (rows.length === 0) return null
    return (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 6, paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows}
        </div>
    )
}

/** Tooltip para gráfico de linha única */
function makeSingleTooltip(chartLabel: string, stats: StatResult | null, enabledStats: Set<string>) {
    return ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null
        return (
            <div style={{ background: '#202833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', fontSize: 11, fontFamily: 'Nunito,sans-serif', minWidth: 120 }}>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontSize: 10 }}>{label}</div>
                <div style={{ color: '#5292D1', display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span>{chartLabel}</span><span>{fmt(Number(payload[0]?.value))}</span>
                </div>
                {stats && enabledStats.size > 0 && <StatRows stats={stats} enabledStats={enabledStats} />}
            </div>
        )
    }
}

/** Tooltip para gráfico multi-série */
function makeMultiTooltip(
    seriesKeys: { key: string; label: string; color: string }[],
    seriesStats: Record<string, StatResult | null>,
    enabledStats: Set<string>
) {
    return ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null
        return (
            <div style={{ background: '#202833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', fontSize: 11, fontFamily: 'Nunito,sans-serif', minWidth: 140 }}>
                <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontSize: 10 }}>{label}</div>
                {payload.map((p: any) => {
                    const sk = seriesKeys.find(s => s.key === p.dataKey)
                    return p.value != null ? (
                        <div key={p.dataKey} style={{ color: sk?.color ?? '#fff', display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                            <span>{sk?.label ?? p.dataKey}</span><span>{fmt(Number(p.value))}</span>
                        </div>
                    ) : null
                })}
                {/* Stats por série */}
                {enabledStats.size > 0 && seriesKeys.map(sk => {
                    const st = seriesStats[sk.key]
                    if (!st) return null
                    return (
                        <div key={`stats-${sk.key}`}>
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 6, paddingTop: 4, color: 'rgba(255,255,255,0.35)', fontSize: 9, letterSpacing: 0.5, marginBottom: 2 }}>
                                {sk.label.toUpperCase()}
                            </div>
                            <StatRows stats={st} enabledStats={enabledStats} />
                        </div>
                    )
                })}
            </div>
        )
    }
}

// ── Label customizado para ReferenceLine ──────────────────────────

const RefLabel: React.FC<{ viewBox?: any; value: string; stroke: string; offset?: number }> = ({
    viewBox, value, stroke, offset = 0
}) => {
    if (!viewBox) return null
    return (
        <text
            x={(viewBox.x ?? 0) + (viewBox.width ?? 0) - 2 + offset}
            y={(viewBox.y ?? 0) - 3}
            fill={stroke}
            fontSize={9}
            fontFamily="Nunito, sans-serif"
            textAnchor="end"
        >
            {value}
        </text>
    )
}

// ── Renderiza reference lines para um conjunto de stats ───────────

function renderStatLines(
    stats: StatResult,
    enabledStats: Set<string>,
    strokeOverride?: string,
    labelSuffix = ''
): React.ReactNode {
    const nodes: React.ReactNode[] = []
    const statKeys: StatKey[] = ['mean', 'median', 'mode', 'max', 'min', 'stdDev']

    for (const key of statKeys) {
        if (!enabledStats.has(key)) continue
        const s = STAT_STYLE[key]
        const stroke = strokeOverride ?? s.stroke

        if (key === 'stdDev') {
            // Duas linhas: mean + σ e mean - σ
            const plus  = stats.mean + stats.stdDev
            const minus = stats.mean - stats.stdDev
            nodes.push(
                <ReferenceLine key={`${key}-plus${labelSuffix}`}  y={plus}  stroke={stroke} strokeDasharray={s.dash} strokeWidth={1} ifOverflow="hidden"
                    label={<RefLabel value={`+σ${labelSuffix}`} stroke={stroke} />} />,
                <ReferenceLine key={`${key}-minus${labelSuffix}`} y={minus} stroke={stroke} strokeDasharray={s.dash} strokeWidth={1} ifOverflow="hidden"
                    label={<RefLabel value={`−σ${labelSuffix}`} stroke={stroke} />} />
            )
        } else {
            nodes.push(
                <ReferenceLine key={`${key}${labelSuffix}`} y={stats[key]} stroke={stroke} strokeDasharray={s.dash} strokeWidth={1} ifOverflow="hidden"
                    label={<RefLabel value={`${s.label}${labelSuffix}`} stroke={stroke} />} />
            )
        }
    }
    return nodes
}

interface Props {
    chart: ChartData
    onRemove: (id: string) => void
    dragHandle?: string
    currentW?: number
    onResizeOne?: (id: string, w: number) => void
    enabledStats: Set<string>
    axisDomain: ['auto' | number, 'auto' | number]
    isMobile?: boolean
    periodoPendente?: boolean
}

function fmt(v: number): string {
    if (isNaN(v)) return ''
    return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

function formatAxisTime(timestamp: string, withDate: boolean): string {
    const d = new Date(timestamp)
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    if (!withDate) return time
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`
}

// ── Label customizado da linha de troca de dia (fica no topo, acima do gráfico) ──
const DayBoundaryLabel: React.FC<{ viewBox?: any; value: string }> = ({ viewBox, value }) => {
    if (!viewBox) return null
    return (
        <text
            x={viewBox.x ?? 0}
            y={(viewBox.y ?? 0) - 3}
            fill="rgba(255,255,255,0.45)"
            fontSize={9}
            fontFamily="Nunito, sans-serif"
            textAnchor="middle"
        >
            {value}
        </text>
    )
}

/** Timestamps (em ordem de exibição) onde o dia-calendário muda em relação ao ponto anterior. */
function dayBoundaries(timestamps: string[]): string[] {
    const boundaries: string[] = []
    for (let i = 1; i < timestamps.length; i++) {
        if (new Date(timestamps[i]).toDateString() !== new Date(timestamps[i - 1]).toDateString()) {
            boundaries.push(timestamps[i])
        }
    }
    return boundaries
}

/** Uma ReferenceLine vertical por troca de dia, com o dia indicado no topo da linha. */
function renderDayBoundaries(timestamps: string[]): React.ReactNode {
    return dayBoundaries(timestamps).map(ts => (
        <ReferenceLine key={`day-${ts}`} x={ts} stroke="rgba(255,255,255,0.18)" strokeDasharray="3 3"
            label={<DayBoundaryLabel value={new Date(ts).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} />} />
    ))
}

// ── Gráfico linha única ───────────────────────────────────────────

const SingleLineChart: React.FC<{ chart: ChartData; enabledStats: Set<string>; axisDomain: ['auto' | number, 'auto' | number] }> = ({ chart, enabledStats, axisDomain }) => {
    // `t` fica com o timestamp bruto (chave única por ponto) — a formatação de exibição vai só no
    // tickFormatter/labelFormatter, senão dois pontos em dias diferentes na mesma hora (ex: "14:25")
    // colidiriam na chave categórica do eixo X.
    const data = useMemo(() => {
        return chart.points.map(p => ({
            t: p.timestamp,
            v: Number(p.value)
        }))
    }, [chart.points])

    // Muda sempre que o range de dados muda (período aplicado) — força o Recharts a remontar o
    // gráfico e descartar qualquer estado interno de hover/tooltip da renderização anterior,
    // evitando tooltip mostrando valor de um ponto que não existe mais no novo período.
    const chartKey = `${chart.points.length}-${chart.points[0]?.timestamp}-${chart.points[chart.points.length - 1]?.timestamp}`

    const stats = useMemo(() => computeStats(chart.points), [chart.points])

    // Tooltip customizado recriado apenas quando stats ou enabledStats mudam
    const CustomTooltip = useMemo(
        () => makeSingleTooltip(chart.label, stats, enabledStats),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [chart.label, stats, enabledStats]
    )

    if (data.length === 0) return (
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', paddingTop: 20 }}>
            Sem dados para o período
        </div>
    )

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart key={chartKey} syncId="dhm-charts" data={data} margin={{ top: 16, right: 32, bottom: 4, left: 0 }}>
                <defs>
                    {/* userSpaceOnUse: com objectBoundingBox (padrão), uma linha reta tem bbox de altura
                        zero e o gradiente degenera — o stroke simplesmente não pinta nada. */}
                    <linearGradient id="chartGrad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100%" y2="0">
                        <stop offset="0%" stopColor="#5292D1" />
                        <stop offset="100%" stopColor="#7BB8E8" />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="t" tickFormatter={(ts: string) => formatAxisTime(ts, false)} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Nunito, sans-serif' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis domain={axisDomain} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Nunito, sans-serif' }} tickLine={false} axisLine={false} tickFormatter={fmt} width={48} />
                <Tooltip content={<CustomTooltip />} labelFormatter={(ts) => formatAxisTime(String(ts), true)} />
                {renderDayBoundaries(chart.points.map(p => p.timestamp))}
                <Line type="monotone" dataKey="v" stroke="url(#chartGrad)" strokeWidth={2} dot={false} isAnimationActive={false} />
                {stats && enabledStats.size > 0 && renderStatLines(stats, enabledStats)}
            </LineChart>
        </ResponsiveContainer>
    )
}

// ── Gráfico multi-série (pressão LD+LE / deslocamento LD+LE) ─────

const MultiSeriesChart: React.FC<{ chart: ChartData; enabledStats: Set<string>; axisDomain: ['auto' | number, 'auto' | number] }> = ({ chart, enabledStats, axisDomain }) => {
    const { data, seriesKeys } = useMemo(() => {
        if (!chart.series?.length) return { data: [], seriesKeys: [] }

        // Merge de todos os timestamps em uma lista ordenada
        const tsSet = new Set<string>()
        chart.series.forEach(s => s.points.forEach(p => tsSet.add(p.timestamp)))
        const sorted = Array.from(tsSet).sort()

        // `t` fica com o timestamp bruto — ver comentário equivalente em SingleLineChart.
        const merged = sorted.map(ts => {
            const row: Record<string, any> = {
                t: ts
            }
            chart.series!.forEach(s => {
                const pt = s.points.find(p => p.timestamp === ts)
                row[s.key] = pt ? Number(pt.value) : null
            })
            return row
        })

        return { data: merged, seriesKeys: chart.series.map(s => ({ key: s.key, label: s.label, color: s.color })) }
    }, [chart.series])

    // Stats por série (cada série tem suas próprias referências com a cor dela)
    const seriesStats = useMemo(() => {
        if (!chart.series?.length) return {}
        return Object.fromEntries(
            chart.series.map(s => [s.key, computeStats(s.points)])
        )
    }, [chart.series])

    const CustomTooltip = useMemo(
        () => makeMultiTooltip(seriesKeys, seriesStats, enabledStats),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [seriesKeys, seriesStats, enabledStats]
    )

    // Mesma lógica do gráfico de linha única — força remount ao trocar o range de dados,
    // descartando estado interno de hover/tooltip obsoleto do Recharts.
    const chartKey = `${data.length}-${data[0]?.t}-${data[data.length - 1]?.t}`

    if (data.length === 0) return (
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', paddingTop: 20 }}>
            Sem dados para o período
        </div>
    )

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart key={chartKey} syncId="dhm-charts" data={data} margin={{ top: 16, right: 36, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="t" tickFormatter={(ts: string) => formatAxisTime(ts, false)} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Nunito, sans-serif' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis domain={axisDomain} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10, fontFamily: 'Nunito, sans-serif' }} tickLine={false} axisLine={false} tickFormatter={fmt} width={48} />
                <Tooltip content={<CustomTooltip />} labelFormatter={(ts) => formatAxisTime(String(ts), true)} />
                {renderDayBoundaries(data.map(d => d.t))}
                <Legend
                    wrapperStyle={{ fontSize: 11, fontFamily: 'Nunito,sans-serif', paddingTop: 4 }}
                    formatter={(value) => {
                        const s = seriesKeys.find(sk => sk.key === value)
                        return <span style={{ color: 'rgba(255,255,255,0.7)' }}>{s?.label ?? value}</span>
                    }}
                />
                {seriesKeys.map(s => (
                    <Line
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        stroke={s.color}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={false}
                    />
                ))}
                {/* Reference lines por série — usa cor da série com opacidade reduzida */}
                {enabledStats.size > 0 && seriesKeys.map(s => {
                    const st = seriesStats[s.key]
                    if (!st) return null
                    // Torna a cor ligeiramente mais transparente para não colidir com as linhas
                    return renderStatLines(st, enabledStats, s.color + 'bb', ` (${s.label})`)
                })}
            </LineChart>
        </ResponsiveContainer>
    )
}

// ── ChartPanel ───────────────────────────────────────────────────

const ChartPanel: React.FC<Props> = ({ chart, onRemove, dragHandle = 'drag-handle', currentW = 6, onResizeOne, enabledStats, axisDomain, isMobile = false, periodoPendente = false }) => {
    const isMultiSeries = !!chart.series?.length
    const chartAreaRef = useRef<HTMLDivElement>(null)
    useDismissTooltipOnOutsideTouch(chartAreaRef)

    const nextSize = () => {
        const idx = CHART_SIZES.indexOf(currentW as ChartSizeW)
        const next = CHART_SIZES[(idx + 1) % CHART_SIZES.length]
        onResizeOne?.(chart.id, next)
    }

    return (
        <div style={CARD_STYLE}>
            <div style={isMobile ? { ...HEADER_STYLE, cursor: 'default' } : HEADER_STYLE} className={isMobile ? undefined : dragHandle}>
                <span style={TITLE_STYLE}>{chart.label}</span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                    {onResizeOne && !isMobile && (
                        <button
                            style={{ ...CLOSE_BTN, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, padding: 0 }}
                            onClick={e => { e.stopPropagation(); nextSize() }}
                            title={SIZE_TIPS[currentW]}
                        >
                            {SIZE_ICONS[currentW]}
                        </button>
                    )}
                    <button style={CLOSE_BTN} onClick={() => onRemove(chart.id)}>✕</button>
                </div>
            </div>
            {periodoPendente && (
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#E5D34A', background: 'rgba(229,211,74,0.1)', borderRadius: 6, padding: '4px 8px', marginBottom: 6, flexShrink: 0 }}>
                    ⚠ Período alterado — clique em "Aplicar período" para atualizar
                </div>
            )}
            <div ref={chartAreaRef} style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
                {chart.loading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                        <style>{CHART_SPIN_KEYFRAMES}</style>
                        <Loader2 size={22} color="#5292D1" style={{ animation: 'dhm-chart-spin 0.8s linear infinite' }} />
                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Carregando...</span>
                    </div>
                )}
                {chart.error && (
                    <div style={{ color: '#F03F3F', fontSize: 12, padding: 8 }}>Erro: {chart.error}</div>
                )}
                {!chart.loading && !chart.error && (
                    isMultiSeries
                        ? <MultiSeriesChart chart={chart} enabledStats={enabledStats} axisDomain={axisDomain} />
                        : <SingleLineChart  chart={chart} enabledStats={enabledStats} axisDomain={axisDomain} />
                )}
            </div>
        </div>
    )
}

export default ChartPanel
